import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { signInPath } from '../utils/authNavigation';
import EmailSignIn from '../pages/EmailSignIn';

// Public pages
import Home from '../pages/Home';
const TryVoting = lazy(() => import('../pages/TryVoting'));
import EventsList from '../pages/EventsList';
import EventDetail from '../pages/EventDetail';
import Login from '../pages/Login';
import Signup from '../pages/Signup';
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const TermsAndConditions = lazy(() => import('../pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const Support = lazy(() => import('../pages/Support'));
const BrandGuide = lazy(() => import('../pages/admin/BrandGuide'));

// Authenticated user pages
const Profile = lazy(() => import('../pages/Profile'));
const MyVotes = lazy(() => import('../pages/MyVotes'));
const SuggestEvent = lazy(() => import('../pages/SuggestEvent'));
const MySuggestions = lazy(() => import('../pages/MySuggestions'));
const VoteSwipe = lazy(() => import('../pages/VoteSwipe'));

// Admin pages
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const AdminEvents = lazy(() => import('../pages/admin/AdminEvents'));
const AdminCreateEvent = lazy(() => import('../pages/admin/AdminCreateEvent'));
const AdminEditEvent = lazy(() => import('../pages/admin/AdminEditEvent'));
const AdminUploadEvents = lazy(() => import('../pages/admin/AdminUploadEvents'));
const AdminUsers = lazy(() => import('../pages/admin/AdminUsers'));
const AdminCategories = lazy(() => import('../pages/admin/AdminCategories'));
const AdminSuggestions = lazy(() => import('../pages/admin/AdminSuggestions'));
const AdminAnalytics = lazy(() => import('../pages/admin/AdminAnalytics'));
const AdminSegmentResults = lazy(() => import('../pages/admin/AdminSegmentResults'));
const AdminMarketing = lazy(() => import('../pages/admin/AdminMarketing'));
const AdminAnnouncements = lazy(() => import('../pages/admin/AdminAnnouncements'));
const AdminSettings = lazy(() => import('../pages/admin/AdminSettings'));
const AdminContactMessages = lazy(() => import('../pages/admin/AdminContactMessages'));

// ---- Route guards ----

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center">
    <p className="text-slate-400">Loading...</p>
  </div>
);

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  return isAuthenticated ? children : <Navigate to={signInPath(location.pathname + location.search)} replace />;
};

export const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return isAdmin ? children : <Navigate to="/" />;
};

// ---- Route tree ----

const AppRoutes = () => (
  <Suspense fallback={<LoadingScreen />}><Routes>
    {/* Public */}
    <Route path="/" element={<Home />} />
    <Route path="/try" element={<TryVoting />} />
    <Route path="/events" element={<EventsList />} />
    <Route path="/events/:eventId" element={<EventDetail />} />
    <Route path="/login" element={<Login />} />
    <Route path="/auth/email" element={<EmailSignIn />} />
    <Route path="/signup" element={<Signup />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/terms" element={<TermsAndConditions />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/support" element={<Support />} />
    <Route path="/brand" element={<BrandGuide />} />

    {/* Authenticated users */}
    <Route path="/vote" element={<ProtectedRoute><VoteSwipe /></ProtectedRoute>} />
    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
    <Route path="/my-votes" element={<ProtectedRoute><MyVotes /></ProtectedRoute>} />
    <Route path="/suggest" element={<ProtectedRoute><SuggestEvent /></ProtectedRoute>} />
    <Route path="/my-suggestions" element={<ProtectedRoute><MySuggestions /></ProtectedRoute>} />

    {/* Admin — nested under AdminDashboard layout (uses <Outlet />) */}
    <Route
      path="/admin"
      element={<AdminRoute><AdminDashboard /></AdminRoute>}
    >
      <Route index element={<div className="text-center py-12 text-slate-400">Select an option above to manage the platform</div>} />
      <Route path="events" element={<AdminEvents />} />
      <Route path="create-event" element={<AdminCreateEvent />} />
      <Route path="edit-event/:eventId" element={<AdminEditEvent />} />
      <Route path="upload-events" element={<AdminUploadEvents />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="categories" element={<AdminCategories />} />
      <Route path="suggestions" element={<AdminSuggestions />} />
      <Route path="analytics" element={<AdminAnalytics />} />
      <Route path="segment-results" element={<AdminSegmentResults />} />
      <Route path="marketing" element={<AdminMarketing />} />
      <Route path="announcements" element={<AdminAnnouncements />} />
      <Route path="contact-messages" element={<AdminContactMessages />} />
      <Route path="settings" element={<AdminSettings />} />
    </Route>
  </Routes></Suspense>
);

export default AppRoutes;
